/**
 * Custom hooks for Document Arrangements API
 *
 * Provides CRUD hooks for managing receiver document arrangements
 * (notify parties, document recipients, stakeholders per receiver + port).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, apiPut } from "@/lib/api-client";
import type {
  ArrangementCreate,
  ArrangementUpdate,
  DocumentArrangement,
  DocumentArrangementsListResponse,
} from "@/types/document-arrangement";

// ============================================================================
// QUERY KEYS
// ============================================================================

const ARRANGEMENTS_QUERY_KEYS = {
  all: ["document-arrangements"] as const,
  lists: () => [...ARRANGEMENTS_QUERY_KEYS.all, "list"] as const,
  list: (filters?: { receiver_name?: string; receiver_company_id?: string }) =>
    [...ARRANGEMENTS_QUERY_KEYS.lists(), filters] as const,
  details: () => [...ARRANGEMENTS_QUERY_KEYS.all, "detail"] as const,
  detail: (id: string) => [...ARRANGEMENTS_QUERY_KEYS.details(), id] as const,
};

// ============================================================================
// LIST ARRANGEMENTS
// ============================================================================

export function useDocumentArrangements(
  receiverName?: string,
  receiverCompanyId?: string,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ARRANGEMENTS_QUERY_KEYS.list({ receiver_name: receiverName, receiver_company_id: receiverCompanyId }),
    queryFn: async (): Promise<DocumentArrangementsListResponse> => {
      const params: Record<string, string> = {};
      if (receiverName) params.receiver_name = receiverName;
      // The API filters by receiver_name; we pass it as search param
      return await apiGet<DocumentArrangementsListResponse>("/document-arrangements", { params });
    },
    enabled: enabled && !!(receiverName || receiverCompanyId),
    staleTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// CREATE ARRANGEMENT
// ============================================================================

export function useCreateArrangement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ArrangementCreate): Promise<DocumentArrangement> => {
      return await apiPost<DocumentArrangement>("/document-arrangements", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ARRANGEMENTS_QUERY_KEYS.lists() });
    },
  });
}

// ============================================================================
// UPDATE ARRANGEMENT
// ============================================================================

export function useUpdateArrangement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      arrangementId,
      data,
    }: {
      arrangementId: string;
      data: ArrangementUpdate;
    }): Promise<DocumentArrangement> => {
      return await apiPut<DocumentArrangement>(
        `/document-arrangements/${arrangementId}`,
        data
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ARRANGEMENTS_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({
        queryKey: ARRANGEMENTS_QUERY_KEYS.detail(variables.arrangementId),
      });
    },
  });
}

// ============================================================================
// DELETE ARRANGEMENT
// ============================================================================

export function useDeleteArrangement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (arrangementId: string): Promise<{ detail: string; arrangement_id: string }> => {
      return await apiDelete<{ detail: string; arrangement_id: string }>(
        `/document-arrangements/${arrangementId}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ARRANGEMENTS_QUERY_KEYS.lists() });
    },
  });
}
